import { createPortal } from "react-dom";
import React, { useState, Fragment, useEffect, useRef, useCallback } from "react";
import { Transition } from "@headlessui/react";
import { XMarkIcon, CheckIcon } from "@heroicons/react/20/solid";
import useApiToken from "../../../hooks/useApiToken";
import { editUniversity, searchCities } from "../../../api/ranking.api";

interface CityOption {
  id: string;
  name: string;
}

interface UniversityEditData {
  id: string;
  name: string;
  url: string;
  linkedIn: string;
  location: string;
  cityId: string | null;
  alternateNames: string;
  package: string;
  recruiters: string;
  degrees: string;
  abbreviation: string;
}

interface Props {
  isOpen: boolean;
  onCancel: () => void;
  onSave: () => void;
  universityData: any;
}

const TagInput = ({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  placeholder: string;
}) => {
  const [inputVal, setInputVal] = useState("");
  const tags = value
    ? value
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
    : [];

  const addTag = () => {
    const trimmed = inputVal.trim();
    if (!trimmed) return;
    const newTags = [...tags, trimmed];
    onChange(newTags.join(", "));
    setInputVal("");
  };

  const removeTag = (index: number) => {
    const newTags = tags.filter((_, i) => i !== index);
    onChange(newTags.join(", "));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag();
    }
  };

  return (
    <div className="flex items-start gap-4">
      <label className="text-sm font-semibold w-[140px] text-gray-700 dark:text-gray-300 mt-1">
        {label}:
      </label>
      <div className="flex-1">
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {tags.map((tag, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 text-xs px-2 py-1 rounded-full"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(i)}
                  className="hover:text-red-500"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center gap-1">
          <input
            type="text"
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="placeholder:text-xs px-2 py-1 flex-1 border-gray-300 rounded-xl text-sm dark:bg-gray-700 dark:text-white"
          />
          <button
            type="button"
            onClick={addTag}
            disabled={!inputVal.trim()}
            className="p-1 rounded-full text-indigo-600 hover:bg-indigo-100 dark:hover:bg-indigo-900 disabled:opacity-30 disabled:cursor-default"
          >
            <CheckIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const EditUniversityModal = ({ isOpen, onCancel, onSave, universityData }: Props) => {
  const token = useApiToken();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState<UniversityEditData>({
    id: "",
    name: "",
    url: "",
    linkedIn: "",
    location: "",
    cityId: null,
    alternateNames: "",
    package: "",
    recruiters: "",
    degrees: "",
    abbreviation: "",
  });

  const [selectedCity, setSelectedCity] = useState<CityOption | null>(null);
  const [citySearch, setCitySearch] = useState("");
  const [cityResults, setCityResults] = useState<CityOption[]>([]);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [searchingCities, setSearchingCities] = useState(false);
  const citySearchRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (universityData && isOpen) {
      const city: CityOption | null =
        universityData.cityId && universityData.cityName
          ? { id: universityData.cityId, name: universityData.cityName }
          : null;
      setFormData({
        id: universityData.id || "",
        name: universityData.name || "",
        url: universityData.url || "",
        linkedIn: universityData.linkedIn || "",
        location: universityData.location || "",
        cityId: city?.id || null,
        alternateNames: universityData.alternateNames || "",
        package: universityData.package || "",
        recruiters: universityData.recruiters || "",
        degrees: universityData.degrees || "",
        abbreviation: universityData.abbreviation || "",
      });
      setSelectedCity(city);
      setError("");
      setCitySearch("");
      setCityResults([]);
    }
  }, [universityData, isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflowY = "hidden";
    } else {
      document.body.style.overflowY = "auto";
    }
    return () => {
      document.body.style.overflowY = "auto";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (citySearchRef.current && !citySearchRef.current.contains(e.target as Node)) {
        setShowCityDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCitySearch = useCallback(
    (query: string) => {
      setCitySearch(query);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (query.trim().length < 2) {
        setCityResults([]);
        setShowCityDropdown(false);
        return;
      }
      debounceRef.current = setTimeout(async () => {
        if (!token) return;
        setSearchingCities(true);
        try {
          const results = await searchCities(query.trim(), token);
          const mapped: CityOption[] = results.map((r: any) => ({
            id: r.id,
            name: r.name,
          }));
          setCityResults(mapped);
          setShowCityDropdown(true);
        } catch {
          setCityResults([]);
        } finally {
          setSearchingCities(false);
        }
      }, 300);
    },
    [token]
  );

  const selectCity = (city: CityOption) => {
    setSelectedCity(city);
    setFormData((prev) => ({ ...prev, cityId: city.id }));
    setCitySearch("");
    setCityResults([]);
    setShowCityDropdown(false);
  };

  const clearCity = () => {
    setSelectedCity(null);
    setFormData((prev) => ({ ...prev, cityId: null }));
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!token) return;
    setSaving(true);
    setError("");
    try {
      await editUniversity(formData, token);
      onSave();
      onCancel();
    } catch (err: any) {
      setError(err.message || "Failed to update university");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <Transition.Root show={isOpen} as={Fragment}>
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        enterTo="opacity-100 translate-y-0 sm:scale-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
      >
        <div
          className={`${
            isOpen ? "transition-all" : "hidden"
          } fixed top-0 right-0 left-0 bottom-0 w-[100vw] z-20`}
        >
          <div
            className="absolute bg-gray-400 opacity-40 top-0 w-full h-screen z-10"
            onClick={onCancel}
          ></div>
          <div
            className="absolute top-0 right-0 bottom-0 left-0 max-w-[550px] max-h-max m-auto z-50 p-4"
          >
            <header className="bg-gray-100 dark:bg-gray-700 rounded-t-xl p-2 text-center text-gray-700 dark:text-gray-400 font-medium">
              Edit University
            </header>
            <section className="flex flex-col bg-white dark:bg-darkbglight p-6 overflow-auto gap-4 max-h-[70vh]">
              <div className="flex items-center gap-4">
                <label className="text-sm font-semibold w-[140px] text-gray-700 dark:text-gray-300">
                  Name:
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="placeholder:text-xs px-2 py-1 flex-1 border-gray-300 rounded-xl text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>

              <TagInput
                label="Abbreviation"
                value={formData.abbreviation}
                onChange={(val) => setFormData((prev) => ({ ...prev, abbreviation: val }))}
                placeholder="e.g. IIT, NIT"
              />

              <TagInput
                label="Alternate Names"
                value={formData.alternateNames}
                onChange={(val) => setFormData((prev) => ({ ...prev, alternateNames: val }))}
                placeholder="Add alternate name..."
              />

              <div className="flex items-center gap-4">
                <label className="text-sm font-semibold w-[140px] text-gray-700 dark:text-gray-300">
                  Website:
                </label>
                <input
                  type="text"
                  name="url"
                  value={formData.url}
                  onChange={handleChange}
                  placeholder="https://example.edu"
                  className="placeholder:text-xs px-2 py-1 flex-1 border-gray-300 rounded-xl text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="text-sm font-semibold w-[140px] text-gray-700 dark:text-gray-300">
                  LinkedIn:
                </label>
                <input
                  type="text"
                  name="linkedIn"
                  value={formData.linkedIn}
                  onChange={handleChange}
                  placeholder="https://linkedin.com/school/..."
                  className="placeholder:text-xs px-2 py-1 flex-1 border-gray-300 rounded-xl text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="flex items-start gap-4">
                <label className="text-sm font-semibold w-[140px] text-gray-700 dark:text-gray-300 mt-1">
                  City:
                </label>
                <div className="flex-1" ref={citySearchRef}>
                  {selectedCity ? (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 text-xs px-2 py-1 rounded-full">
                        {selectedCity.name}
                        <button
                          type="button"
                          onClick={clearCity}
                          className="hover:text-red-500"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    </div>
                  ) : (
                    <div className="relative">
                      <input
                        type="text"
                        value={citySearch}
                        onChange={(e) => handleCitySearch(e.target.value)}
                        placeholder="Search city..."
                        className="placeholder:text-xs px-2 py-1 w-full border-gray-300 rounded-xl text-sm dark:bg-gray-700 dark:text-white"
                      />
                      {showCityDropdown && (
                        <div className="absolute z-50 mt-1 w-full max-h-40 overflow-auto bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl shadow-lg">
                          {searchingCities ? (
                            <div className="px-3 py-2 text-xs text-gray-500">Searching...</div>
                          ) : cityResults.length > 0 ? (
                            cityResults.map((city) => (
                              <button
                                key={city.id}
                                type="button"
                                onClick={() => selectCity(city)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 dark:hover:bg-gray-600 dark:text-white"
                              >
                                {city.name}
                              </button>
                            ))
                          ) : (
                            <div className="px-3 py-2 text-xs text-gray-500">No cities found</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* <div className="flex items-center gap-4">
                <label className="text-sm font-semibold w-[140px] text-gray-700 dark:text-gray-300">
                  Location:
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="e.g. Mumbai, Maharashtra"
                  className="placeholder:text-xs px-2 py-1 flex-1 border-gray-300 rounded-xl text-sm dark:bg-gray-700 dark:text-white"
                />
              </div> */}

              <div className="flex items-center gap-4">
                <label className="text-sm font-semibold w-[140px] text-gray-700 dark:text-gray-300">
                  Package:
                </label>
                <input
                  type="text"
                  name="package"
                  value={formData.package}
                  onChange={handleChange}
                  placeholder="e.g. 12 LPA"
                  className="placeholder:text-xs px-2 py-1 flex-1 border-gray-300 rounded-xl text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>

              <TagInput
                label="Recruiters"
                value={formData.recruiters}
                onChange={(val) => setFormData((prev) => ({ ...prev, recruiters: val }))}
                placeholder="Add recruiter..."
              />

              <TagInput
                label="Degrees"
                value={formData.degrees}
                onChange={(val) => setFormData((prev) => ({ ...prev, degrees: val }))}
                placeholder="e.g. B.Tech, M.Tech"
              />

              {error && (
                <span className="text-xs text-red-500">*{error}</span>
              )}
            </section>
            <footer className="bg-gray-100 dark:bg-gray-700 rounded-b-xl p-2 text-center text-gray-700 dark:text-gray-400 font-medium">
              <div className="flex justify-end gap-3">
                <button
                  className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${
                    saving ? "bg-gray-400" : "bg-indigo-600 hover:bg-indigo-700"
                  }`}
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
                <button
                  className="inline-flex items-center rounded-md bg-red-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  onClick={onCancel}
                >
                  Cancel
                </button>
              </div>
            </footer>
          </div>
        </div>
      </Transition.Child>
    </Transition.Root>,
    document.body
  );
};

export default EditUniversityModal;
